import { Component } from "react";
import Helmet from "react-helmet";
import AreaAnalysis from "src/components/areas/areaAnalysisDescription/AreaAnalysis";
import Area from "src/components/areas/areaAnalysisDescription/Area";
import type { AnalysisBuilderChain } from "src/components/areas/areaAnalysisDescription/analysis_builders/AnalysisBuilderChain";
import AreaTitleVariantSelector from "src/components/areas/seo/AreaTitleVariantSelector";

interface AreaAnalysisPageSEOProps {
    areaId: string;
    area: any;
    areaAnalysisBuilderChain: AnalysisBuilderChain;
}

interface AreaAnalysisPageSEOState {}

class AreaAnalysisPageSEO extends Component<AreaAnalysisPageSEOProps, AreaAnalysisPageSEOState> {
    private readonly BASE_URL = "https://homeindex.ie";

    formatCurrencyForSEO = (amount: number): string => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            useGrouping: true // Commas between thousands
        }).format(amount);
    }

    formatCurrencyForTitle = (amount: number): string => {
        if (amount >= 1000) {
            const thousands = Math.round(amount / 1000);
            return `€${thousands}K`;
        }
        return `€${amount}`;
    }

    buildAnalysisDescription = (area: any, analysis: any): string => {
        const safeArea = area != null && typeof area === "object" ? (area as Record<string, unknown>) : {};
        const safeAnalysis = analysis != null && typeof analysis === "object" ? (analysis as Record<string, unknown>) : {};

        try {
            const typedAnalysis = AreaAnalysis.fromDict(safeAnalysis);
            const typedArea = Area.fromDict(safeArea);
            return this.props.areaAnalysisBuilderChain.build(typedAnalysis, typedArea).trim();
        } catch {
            return "";
        }
    }

    buildOrganizationStructuredData = (country: string) => {
        return {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "HomeIndex",
            "url": this.BASE_URL,
            "description": "Independent property valuation service",
            "address": {
                "@type": "PostalAddress",
                "addressCountry": country
            }
        };
    }

    buildAreaStructuredData = (
        area: any,
        analysis: any,
        description: string,
        canonicalUrl: string
    ) => {
        if (!area) {
            return {
                "@context": "https://schema.org",
                "@type": "Place",
                "name": "",
                "description": description
            };
        }

        const neighbourhood = area["n"] || area["neighbourhood"] || "";
        const region = area["r"] || area["region"] || "";
        const country = area["c"] || area["country"] || "Ireland";
        const coordinates = analysis["gc"] || area["gc"];
        const medianPrice = analysis["1ym"];

        const areaStructuredData: any = {
            "@context": "https://schema.org",
            "@type": "Place",
            "name": neighbourhood || region || "Area",
            "description": description,
            "url": canonicalUrl,
            "address": {
                "@type": "PostalAddress",
                "addressCountry": country
            }
        };

        // Add detailed address components
        if (neighbourhood && neighbourhood.length > 0) {
            areaStructuredData.address.addressLocality = neighbourhood;
        }
        if (region && region.length > 0) {
            areaStructuredData.address.addressRegion = region;
        }

        // Add GeoCoordinates
        if (coordinates && typeof coordinates === "object" && typeof coordinates.lt !== "undefined" && typeof coordinates.ln !== "undefined") {
            areaStructuredData.geo = {
                "@type": "GeoCoordinates",
                "latitude": parseFloat(String(coordinates.lt)),
                "longitude": parseFloat(String(coordinates.ln))
            };
        }

        // Add price range if available
        if (medianPrice && typeof medianPrice === "object" && medianPrice.v) {
            const medianValue = typeof medianPrice.v === "number" ? medianPrice.v : parseFloat(String(medianPrice.v));
            if (medianValue > 0) {
                areaStructuredData.priceRange = this.formatCurrencyForSEO(medianValue);
            }
        }

        return areaStructuredData;
    }

    buildTitle = (areaId: string, neighbourhood: string, region: string): string => {
        const areaName = neighbourhood || region || "Area";
        const areaRegion = region || "Ireland";
        return AreaTitleVariantSelector.getTitle(areaId, areaName, areaRegion);
    }

    buildDescription = (
        area: any,
        analysis: any,
        formattedMedianPrice: string,
        country: string,
        salesCount: number,
        analysisDescription: string
    ): string => {
        const neighbourhood = area["n"] || area["neighbourhood"] || "";
        const region = area["r"] || area["region"] || "";
        const areaName = neighbourhood || region || "Area";

        const parts: string[] = [];

        if (formattedMedianPrice) {
            parts.push(`Median price ${formattedMedianPrice}`);
        }

        if (salesCount > 0) {
            parts.push(`${salesCount} sales in the last year`);
        }

        if (analysisDescription && analysisDescription.length > 0) {
            // Use the analysis description but truncate if too long
            const maxDescriptionLength = 200;
            if (analysisDescription.length > maxDescriptionLength) {
                const truncated = analysisDescription.substring(0, maxDescriptionLength).trim();
                const lastSpace = truncated.lastIndexOf(" ");
                parts.push(lastSpace > 0 ? truncated.substring(0, lastSpace) + "..." : truncated + "...");
            } else {
                parts.push(analysisDescription);
            }
        }

        parts.push(`${areaName}, ${region ? `${region}, ` : ""}${country}`);
        parts.push("HomeIndex Ireland's Area Property Analysis");

        return parts.join(" · ");
    }

    buildCanonicalUrl = (areaId: string, areaSlug: string | null): string => {
        if (areaSlug) {
            return `${this.BASE_URL}/area/${encodeURIComponent(areaId)}/${encodeURIComponent(areaSlug)}`;
        }
        return `${this.BASE_URL}/area/${encodeURIComponent(areaId)}`;
    }

    buildKeywords = (
        neighbourhood: string,
        region: string,
        country: string,
        formattedMedianPrice: string
    ): string => {
        const keywords = [
            "area analysis",
            "property prices",
            "house prices",
            neighbourhood,
            region,
            country,
            "real estate",
            "property market",
            "area statistics"
        ];

        if (formattedMedianPrice) {
            keywords.push(`${neighbourhood || region} property prices`);
            keywords.push(`${neighbourhood || region} house values`);
        }

        if (region && region.length > 0 && region !== neighbourhood) {
            keywords.push(`${region} property market`);
            keywords.push(`${region} real estate`);
        }

        return keywords.filter(k => k && k.length > 0).join(", ");
    }

    render() {
        const { areaId, area, areaAnalysisBuilderChain } = this.props;

        if (!area) {
            return <div></div>;
        }

        const neighbourhood = area["n"] || area["neighbourhood"] || "";
        const region = area["r"] || area["region"] || "";
        const country = area["c"] || area["country"] || "Ireland";
        const areaSlug = area["aurl"] || null;
        const analysis = area["a"] || {};
        const coordinates = analysis["gc"] || area["gc"];
        const medianPrice = analysis["1ym"];
        const salesCount = analysis["1ys"] || 0;

        // Build analysis description using the chain
        const analysisDescription = this.buildAnalysisDescription(area, analysis);

        // Format median price
        let formattedMedianPriceForTitle = "";
        let formattedMedianPriceForDescription = "";
        if (medianPrice && typeof medianPrice === "object" && medianPrice.v) {
            const medianValue = typeof medianPrice.v === "number" ? medianPrice.v : parseFloat(String(medianPrice.v));
            if (medianValue > 0) {
                formattedMedianPriceForTitle = this.formatCurrencyForTitle(medianValue);
                formattedMedianPriceForDescription = this.formatCurrencyForSEO(medianValue);
            }
        }

        const title = this.buildTitle(areaId, neighbourhood, region);
        const description = this.buildDescription(
            area,
            analysis,
            formattedMedianPriceForDescription,
            country,
            salesCount,
            analysisDescription
        );
        const canonicalUrl = this.buildCanonicalUrl(areaId, areaSlug);
        const keywords = this.buildKeywords(neighbourhood, region, country, formattedMedianPriceForDescription);
        const geoRegion = country === "Ireland" ? "IE" : country;
        const areaName = neighbourhood || region || "Area";

        const organizationStructuredData = this.buildOrganizationStructuredData(country);
        const areaStructuredData = this.buildAreaStructuredData(
            area,
            analysis,
            description,
            canonicalUrl
        );

        // Determine if page should be indexed (index if we have sales data)
        const shouldIndex = salesCount > 0;

        return (
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description}/>
                <meta name="keywords" content={keywords}/>
                <link rel="canonical" href={canonicalUrl}/>
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website"/>
                <meta property="og:url" content={canonicalUrl}/>
                <meta property="og:title" content={title}/>
                <meta property="og:description" content={description}/>
                <meta property="og:site_name" content="HomeIndex"/>
                <meta property="og:locale" content="en_IE"/>
                
                {/* Twitter Card */}
                <meta name="twitter:card" content="summary"/>
                <meta name="twitter:title" content={title}/>
                <meta name="twitter:description" content={description}/>
                <meta name="twitter:site" content="@homeindex"/>
                
                {/* Additional SEO meta tags */}
                <meta name="robots" content={shouldIndex ? "index, follow" : "noindex, nofollow"}/>
                <meta name="author" content="HomeIndex"/>
                <meta name="geo.region" content={geoRegion}/>
                <meta name="geo.placename" content={areaName}/>
                {coordinates && typeof coordinates === "object" && typeof coordinates.lt !== "undefined" && typeof coordinates.ln !== "undefined" ? (
                    <meta name="geo.position" content={String(coordinates.lt) + ";" + String(coordinates.ln)}/>
                ) : null}
                {coordinates && typeof coordinates === "object" && typeof coordinates.lt !== "undefined" && typeof coordinates.ln !== "undefined" ? (
                    <meta name="ICBM" content={String(coordinates.lt) + ", " + String(coordinates.ln)}/>
                ) : null}
                {neighbourhood.length > 0 ? (
                    <meta name="geo.locality" content={neighbourhood}/>
                ) : null}
                
                {/* Structured Data */}
                <script 
                    type="application/ld+json" 
                    dangerouslySetInnerHTML={{__html: JSON.stringify(areaStructuredData)}}
                />
                <script 
                    type="application/ld+json" 
                    dangerouslySetInnerHTML={{__html: JSON.stringify(organizationStructuredData)}}
                />
            </Helmet>
        );
    }
}

export default AreaAnalysisPageSEO;
